import React, { useMemo } from 'react';
import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import BlockIcon from '@mui/icons-material/Block';
import UndoIcon from '@mui/icons-material/Undo';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const SEV_COLOR = { critical: 'error', error: 'error', warning: 'warning', info: 'info' };
const money = (v) => (v == null ? '' : `$${Number(v).toFixed(2)}`);
const mapped = (s) => s === 'matched' || s === 'matched_alias';

function MappingCell({ value, status }) {
  if (mapped(status)) {
    return (
      <Tooltip title={status === 'matched_alias' ? 'Matched via alias' : 'Matched'}>
        <span>{value || '—'}</span>
      </Tooltip>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>{value || '—'}</span>
      <Chip label={status || 'unmatched'} color="warning" size="small" variant="outlined" />
    </Box>
  );
}

/**
 * Editable review grid (MUI X DataGrid, Community). Inline-edits payable_minutes
 * via processRowUpdate; surfaces flag severity + mapping status; offers
 * per-row exclude/include and map actions. When `editable` is false (batch
 * submitted/locked) edits and actions are disabled.
 *
 * NOTE: column freeze / row pinning are Pro features and intentionally omitted;
 * identifier columns are ordered first as a Community-friendly substitute.
 */
function ReviewGrid({
  rows,
  loading,
  editable,
  financeMode,
  onCommitEdit,
  onProcessRowUpdateError,
  onExclude,
  onInclude,
  onMapProvider,
  onMapService,
  onFinanceApprove,
  onFinanceReject,
  selectionModel,
  onSelectionModelChange,
}) {
  const columns = useMemo(() => [
    { field: 'source_row_number', headerName: '#', width: 70, type: 'number' },
    {
      field: 'flag_severity',
      headerName: 'Flag',
      width: 110,
      renderCell: (p) => (p.value ? <Chip label={p.value} color={SEV_COLOR[p.value] || 'default'} size="small" /> : null),
    },
    { field: 'status', headerName: 'Status', width: 150 },
    {
      field: 'uploaded_provider_name',
      headerName: 'Provider',
      width: 220,
      renderCell: (p) => <MappingCell value={p.value} status={p.row.provider_mapping_status} />,
    },
    {
      field: 'uploaded_service_type',
      headerName: 'Service type',
      width: 200,
      renderCell: (p) => <MappingCell value={p.value} status={p.row.service_type_mapping_status} />,
    },
    {
      field: 'payable_minutes',
      headerName: 'Payable min',
      width: 130,
      type: 'number',
      editable: true,
      cellClassName: 'editable-cell',
    },
    { field: 'svc_pay_minutes', headerName: 'SVC PAY min', width: 120, type: 'number' },
    { field: 'applied_rate', headerName: 'Rate', width: 100, type: 'number', valueFormatter: (v) => money(v) },
    {
      field: 'calculated_payment_amount',
      headerName: 'Amount',
      width: 120,
      type: 'number',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'is_excluded',
      headerName: 'Excluded',
      width: 100,
      renderCell: (p) => (p.value ? <Chip label="Excluded" size="small" /> : null),
    },
    { field: 'logistixai_order_number', headerName: 'Order #', width: 150 },
    { field: 'source_id', headerName: 'Source ID', width: 140 },
    { field: 'order_status', headerName: 'Order status', width: 140 },
    { field: 'customer_name', headerName: 'Customer', width: 180 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => {
        if (financeMode) {
          if (params.row.is_excluded) return [];
          return [
            <GridActionsCellItem icon={<Tooltip title="Approve row"><CheckCircleIcon color="success" /></Tooltip>} label="Approve" onClick={() => onFinanceApprove(params.row)} />,
            <GridActionsCellItem icon={<Tooltip title="Reject row"><CancelIcon color="error" /></Tooltip>} label="Reject" onClick={() => onFinanceReject(params.row)} />,
          ];
        }
        if (!editable) return [];
        const items = [];
        if (params.row.is_excluded) {
          items.push(<GridActionsCellItem icon={<Tooltip title="Re-include"><UndoIcon /></Tooltip>} label="Include" onClick={() => onInclude(params.row)} />);
        } else {
          items.push(<GridActionsCellItem icon={<Tooltip title="Exclude from payment"><BlockIcon /></Tooltip>} label="Exclude" onClick={() => onExclude(params.row)} />);
        }
        if (!mapped(params.row.provider_mapping_status)) {
          items.push(<GridActionsCellItem icon={<Tooltip title="Map provider"><LinkIcon /></Tooltip>} label="Map provider" onClick={() => onMapProvider(params.row)} showInMenu />);
        }
        if (!mapped(params.row.service_type_mapping_status)) {
          items.push(<GridActionsCellItem icon={<Tooltip title="Map service type"><LinkIcon /></Tooltip>} label="Map service type" onClick={() => onMapService(params.row)} showInMenu />);
        }
        return items;
      },
    },
  ], [editable, financeMode, onExclude, onInclude, onMapProvider, onMapService, onFinanceApprove, onFinanceReject]);

  const processRowUpdate = async (newRow, oldRow) => {
    if (Number(newRow.payable_minutes ?? NaN) !== Number(oldRow.payable_minutes ?? NaN)) {
      const updated = await onCommitEdit(newRow.id, { payable_minutes: newRow.payable_minutes });
      return updated ? { ...newRow, ...updated } : newRow;
    }
    return newRow;
  };

  return (
    <Box sx={{ height: 640, width: '100%', '& .editable-cell': { backgroundColor: 'rgba(25,118,210,0.06)' } }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={(r) => r.id}
        density="compact"
        editMode="cell"
        isCellEditable={() => editable}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        checkboxSelection={editable}
        disableRowSelectionOnClick
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={onSelectionModelChange}
        slots={{ toolbar: GridToolbar }}
        slotProps={{ toolbar: { showQuickFilter: true, printOptions: { disableToolbarButton: true } } }}
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
          columns: {
            columnVisibilityModel: {
              source_id: false,
              logistixai_order_number: false,
              order_status: false,
              customer_name: false,
            },
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        getRowClassName={(p) => (p.row.is_excluded ? 'excluded-row' : '')}
        sx={{ '& .excluded-row': { opacity: 0.55 } }}
      />
    </Box>
  );
}

export default ReviewGrid;
